import { createFileRoute, Link } from "@tanstack/react-router";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useEffect, useMemo, useState } from "react";
import { adminApi, type AdminProject } from "@/api/admin";
import { plansApi } from "@/api/plans";
import { Dialog } from "@/components/ui/Dialog";

export const Route = createFileRoute("/admin/projects/$pid")({
  component: AdminProjectDetail,
});

type Tab = "overview" | "members" | "usage" | "payments" | "ips";
const TABS: Array<{ key: Tab; label: string }> = [
  { key: "overview", label: "Overview" },
  { key: "members", label: "Members" },
  { key: "usage", label: "Usage" },
  { key: "payments", label: "Payments" },
  { key: "ips", label: "IPs" },
];

function AdminProjectDetail() {
  const { pid } = Route.useParams();
  const qc = useQueryClient();
  const [tab, setTab] = useState<Tab>("overview");

  const { data: project, isLoading } = useQuery({
    queryKey: ["admin-project", pid],
    queryFn: () => adminApi.getProject(pid),
  });

  if (isLoading) return <p className="text-ink/55">Loading…</p>;
  if (!project) return <p className="text-ink/55">Not found.</p>;

  return (
    <div className="space-y-6">
      <div>
        <Link to="/admin" className="text-sm text-ink/55 hover:text-ink">← All projects</Link>
        <h1 className="mt-2 text-2xl font-semibold">{project.name}</h1>
        <p className="mt-1 text-sm text-ink/55">
          <code className="font-mono text-xs">{project.id}</code>
          {project.suspended && (
            <span className="ml-3 rounded bg-red-500/15 px-2 py-0.5 text-xs font-medium text-red-600">suspended</span>
          )}
        </p>
      </div>

      <div className="flex gap-1 border-b border-ink/10">
        {TABS.map((t) => (
          <button
            key={t.key}
            onClick={() => setTab(t.key)}
            className={
              "border-b-2 px-3 py-2 text-sm transition " +
              (tab === t.key
                ? "border-ink font-medium text-ink"
                : "border-transparent text-ink/55 hover:text-ink")
            }
          >
            {t.label}
          </button>
        ))}
      </div>

      {tab === "overview" && <OverviewTab pid={pid} />}
      {tab === "members" && <MembersTab pid={pid} />}
      {tab === "usage" && <UsageTab pid={pid} />}
      {tab === "payments" && <PaymentsTab pid={pid} onChanged={() => qc.invalidateQueries({ queryKey: ["admin-project", pid] })} />}
      {tab === "ips" && <IpsTab pid={pid} />}
    </div>
  );
}

// ── Overview ──────────────────────────────────────────────────────────────
function OverviewTab({ pid }: { pid: string }) {
  const qc = useQueryClient();
  const { data: project } = useQuery({ queryKey: ["admin-project", pid], queryFn: () => adminApi.getProject(pid) });
  const { data: plans } = useQuery({ queryKey: ["plans"], queryFn: plansApi.list });

  const invalidate = () =>
    Promise.all([
      qc.invalidateQueries({ queryKey: ["admin-project", pid] }),
      qc.invalidateQueries({ queryKey: ["admin-projects"] }),
      qc.invalidateQueries({ queryKey: ["admin-project-payments", pid] }),
    ]);

  const suspend = useMutation({ mutationFn: (reason: string) => adminApi.suspend(pid, reason), onSuccess: invalidate });
  const unsuspend = useMutation({ mutationFn: () => adminApi.unsuspend(pid), onSuccess: invalidate });
  const setPlan = useMutation({ mutationFn: (planId: string) => adminApi.setPlan(pid, planId), onSuccess: invalidate });
  const setPaid = useMutation({ mutationFn: (date: string) => adminApi.setPaidUntil(pid, date), onSuccess: invalidate });

  const [paymentOpen, setPaymentOpen] = useState(false);
  const [transferOpen, setTransferOpen] = useState(false);

  if (!project) return null;

  return (
    <div className="grid gap-6 lg:grid-cols-2">
      <section className="card">
        <h2 className="text-sm font-medium">Status</h2>
        <dl className="mt-3 space-y-2 text-sm">
          <Row label="Owner" value={project.owner_email ?? "—"} />
          <Row label="Members" value={project.member_count} />
          <Row label="Plan" value={project.plan_id} />
          <Row label="Paid until" value={project.paid_until ? new Date(project.paid_until).toLocaleString() : "—"} />
          <Row label="Created" value={new Date(project.created_at).toLocaleDateString()} />
        </dl>
        <div className="mt-4 flex gap-2">
          {project.suspended ? (
            <button className="btn-primary" onClick={() => unsuspend.mutate()}>Unsuspend</button>
          ) : (
            <button
              className="btn-danger"
              onClick={() => {
                const reason = prompt("Reason for suspension (optional):") ?? "";
                suspend.mutate(reason);
              }}
            >Suspend</button>
          )}
          <button className="btn-secondary" onClick={() => setTransferOpen(true)}>Transfer owner…</button>
        </div>
      </section>

      <section className="card">
        <h2 className="text-sm font-medium">Plan & billing</h2>
        <div className="mt-4 space-y-3">
          <div>
            <label className="label">Plan</label>
            <select className="input mt-1" value={project.plan_id} onChange={(e) => setPlan.mutate(e.target.value)}>
              {plans?.map((p) => <option key={p.id} value={p.id}>{p.display_name}</option>)}
            </select>
          </div>
          <div>
            <label className="label">Paid until</label>
            <input
              type="date"
              className="input mt-1"
              defaultValue={project.paid_until ? project.paid_until.slice(0, 10) : ""}
              onBlur={(e) => {
                if (e.target.value) {
                  const iso = new Date(e.target.value).toISOString();
                  if (iso !== project.paid_until) setPaid.mutate(iso);
                }
              }}
            />
          </div>
          <button className="btn-primary mt-2" onClick={() => setPaymentOpen(true)}>Record payment</button>
        </div>
      </section>

      <PlanOverridesCard project={project} plans={plans ?? []} />

      <RecordPaymentDialog pid={pid} open={paymentOpen} onOpenChange={setPaymentOpen} onDone={invalidate} planOptions={plans?.map((p) => p.id) ?? []} />
      <TransferOwnerDialog pid={pid} open={transferOpen} onOpenChange={setTransferOpen} onDone={invalidate} />
    </div>
  );
}

// ── Plan config (snapshot vs current plan) ───────────────────────────────
// projects.* snapshot the plan terms at buy/upgrade time (Pattern A) so
// later edits to plans.* don't retroactively change a customer's terms.
// This card surfaces the snapshot + flags any field that has since drifted
// from the current plan row.
// Structural type covering the fields we read — works whether `plans` is
// AdminPlan[] (admin endpoint) or Plan[] (public endpoint). Keeps this card
// decoupled from where the plan list comes from.
type PlanForCompare = {
  id: string;
  monthly_credits: string | number;
  cycle_days: number;
  rps_limit: number;
  sse_connections_max: number;
  max_tokens_per_sse_conn: number;
  monthly_price_usd: string | number;
  overage_credit_price_usd: string | number;
};

// Editable numeric/bigint snapshot fields. (Overage is handled separately —
// it needs the mode picker.)
const PLAN_FIELDS = [
  { key: "monthly_credits", label: "Monthly credits", planKey: "monthly_credits" },
  { key: "cycle_days", label: "Cycle days", planKey: "cycle_days" },
  { key: "rps_limit", label: "Req / sec", planKey: "rps_limit" },
  { key: "sse_connections_max", label: "SSE conns", planKey: "sse_connections_max" },
  { key: "max_tokens_per_sse_conn", label: "Tokens / SSE", planKey: "max_tokens_per_sse_conn" },
  { key: "monthly_price_usd", label: "Monthly price (USD)", planKey: "monthly_price_usd" },
] as const;

type PlanFormKey =
  | "monthly_credits" | "cycle_days" | "rps_limit" | "sse_connections_max"
  | "max_tokens_per_sse_conn" | "monthly_price_usd" | "overage_credit_price_usd";

function PlanOverridesCard({
  project, plans,
}: {
  project: AdminProject;
  plans: PlanForCompare[];
}) {
  const qc = useQueryClient();
  const plan = plans.find((p) => p.id === project.plan_id);

  // Initialise the form from the project snapshot. Re-sync whenever the
  // project query refetches (e.g. after a save).
  const initial = useMemo<Record<PlanFormKey, string>>(() => ({
    monthly_credits: project.monthly_credits ?? "",
    cycle_days: project.cycle_days != null ? String(project.cycle_days) : "",
    rps_limit: project.rps_limit != null ? String(project.rps_limit) : "",
    sse_connections_max: project.sse_connections_max != null ? String(project.sse_connections_max) : "",
    max_tokens_per_sse_conn: project.max_tokens_per_sse_conn != null ? String(project.max_tokens_per_sse_conn) : "",
    monthly_price_usd: project.monthly_price_usd ?? "",
    overage_credit_price_usd: project.overage_credit_price_usd ?? "",
  }), [project]);
  const [form, setForm] = useState(initial);
  useEffect(() => { setForm(initial); }, [initial]);
  const set = (k: PlanFormKey, v: string) => setForm((f) => ({ ...f, [k]: v }));

  const dirty = (Object.keys(form) as PlanFormKey[]).some((k) => form[k] !== initial[k]);

  const save = useMutation({
    mutationFn: () => {
      // Send only changed, non-empty fields. Ints go as numbers; bigint /
      // money / overage go as decimal strings (the backend parses them).
      const patch: Record<string, string | number> = {};
      const intKeys: PlanFormKey[] = ["cycle_days", "rps_limit", "sse_connections_max", "max_tokens_per_sse_conn"];
      for (const k of Object.keys(form) as PlanFormKey[]) {
        if (form[k] === initial[k] || form[k] === "") continue;
        patch[k] = intKeys.includes(k) ? Number(form[k]) : form[k];
      }
      return adminApi.editPlanConfig(project.id, patch);
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ["admin-project", project.id] }),
  });

  // Overage mode: "-1" no cap, "0" hard cap, ">0" soft cap @ $X/credit.
  const overageNum = Number(form.overage_credit_price_usd);
  const overageMode =
    form.overage_credit_price_usd === "" ? "" :
    overageNum === -1 ? "nocap" :
    overageNum === 0 ? "hardcap" : "softcap";

  const fillFromPlan = () => {
    if (!plan) return;
    setForm({
      monthly_credits: String(plan.monthly_credits),
      cycle_days: String(plan.cycle_days),
      rps_limit: String(plan.rps_limit),
      sse_connections_max: String(plan.sse_connections_max),
      max_tokens_per_sse_conn: String(plan.max_tokens_per_sse_conn),
      monthly_price_usd: String(plan.monthly_price_usd),
      overage_credit_price_usd: String(plan.overage_credit_price_usd),
    });
  };

  return (
    <section className="card lg:col-span-2">
      <div className="flex items-start justify-between gap-4">
        <div>
          <h2 className="text-sm font-medium">Plan config (per-project override)</h2>
          <p className="mt-1 text-xs text-ink/55 text-pretty">
            Snapshotted on the project row at buy / upgrade, but editable here —
            the gate, usage metering and renewal price all read these values, not{" "}
            <code className="font-mono">plans.{project.plan_id}</code>. The plan column
            shows the current plan for comparison.
          </p>
        </div>
        {plan && (
          <button type="button" className="btn-secondary shrink-0 text-xs" onClick={fillFromPlan}>
            Fill from current plan
          </button>
        )}
      </div>
      <div className="mt-4 overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="text-left text-xs uppercase tracking-wide text-ink/55">
            <tr>
              <th className="py-2 pr-4">Field</th>
              <th className="py-2 pr-4">On this project</th>
              <th className="py-2">Current plan</th>
            </tr>
          </thead>
          <tbody>
            {PLAN_FIELDS.map((f) => {
              const planVal = plan ? (plan as Record<string, string | number>)[f.planKey] : undefined;
              const drifted = planVal != null && form[f.key] !== "" && String(planVal) !== form[f.key];
              return (
                <tr key={f.key} className="border-t border-ink/8">
                  <td className="py-2 pr-4 text-ink/75">{f.label}</td>
                  <td className="py-2 pr-4">
                    <input
                      type="number"
                      className={`input w-40 font-mono text-sm ${drifted ? "border-peach-500/60" : ""}`}
                      value={form[f.key]}
                      onChange={(e) => set(f.key, e.target.value)}
                    />
                  </td>
                  <td className="py-2 font-mono text-ink/55">{planVal == null ? "—" : String(planVal)}</td>
                </tr>
              );
            })}
            <tr className="border-t border-ink/8">
              <td className="py-2 pr-4 text-ink/75">Overage $/credit</td>
              <td className="py-2 pr-4">
                <div className="flex items-center gap-2">
                  <select
                    className="input w-32 text-sm"
                    value={overageMode}
                    onChange={(e) => {
                      const m = e.target.value;
                      set("overage_credit_price_usd", m === "nocap" ? "-1" : m === "hardcap" ? "0" : overageNum > 0 ? form.overage_credit_price_usd : "0.001");
                    }}
                  >
                    <option value="nocap">no cap</option>
                    <option value="hardcap">hard cap</option>
                    <option value="softcap">soft cap</option>
                  </select>
                  {overageMode === "softcap" && (
                    <input
                      type="number"
                      step="0.000001"
                      min="0.000001"
                      className="input w-32 font-mono text-sm"
                      value={form.overage_credit_price_usd}
                      onChange={(e) => set("overage_credit_price_usd", e.target.value)}
                    />
                  )}
                </div>
              </td>
              <td className="py-2 font-mono text-ink/55">{formatOverage(plan?.overage_credit_price_usd) ?? "—"}</td>
            </tr>
          </tbody>
        </table>
      </div>
      <div className="mt-4 flex items-center gap-3">
        <button className="btn-primary" disabled={!dirty || save.isPending} onClick={() => save.mutate()}>
          {save.isPending ? "Saving…" : "Save changes"}
        </button>
        {dirty && !save.isPending && (
          <button className="btn-secondary text-sm" onClick={() => setForm(initial)}>Discard</button>
        )}
        {save.isError && <span className="text-sm text-red-600">{(save.error as Error).message}</span>}
        {save.isSuccess && !dirty && <span className="text-sm text-green-600">Saved.</span>}
      </div>
      <p className="mt-4 text-xs leading-relaxed text-ink/55">
        <span className="font-semibold text-ink/75">Overage mode:</span>{" "}
        <code className="font-mono">no cap</code> = overage always allowed,{" "}
        <code className="font-mono">hard cap</code> = block with <code className="font-mono">429 quota_exhausted</code> on quota exhaustion,{" "}
        <code className="font-mono">soft cap</code> = overage billed at $X per credit.{" "}
        Credits / rps / SSE apply immediately; cycle days + price apply to the next renewal.
      </p>
    </section>
  );
}

// Render the raw overage_credit_price_usd value with its semantic mode so
// the user doesn't have to remember the -1 / 0 / >0 encoding.
function formatOverage(v: string | number | undefined | null): string | undefined {
  if (v == null) return undefined;
  const n = Number(v);
  if (!Number.isFinite(n)) return String(v);
  if (n === -1) return "no cap";
  if (n === 0) return "hard cap";
  return `$${n.toFixed(6).replace(/\.?0+$/, "")} / credit`;
}

// ── Members ─────────────────────────────────────────────────────────────────
function MembersTab({ pid }: { pid: string }) {
  const { data, isLoading } = useQuery({
    queryKey: ["admin-project-members", pid],
    queryFn: () => adminApi.projectMembers(pid),
  });
  return (
    <div className="card overflow-x-auto p-0">
      <table className="w-full text-sm">
        <thead className="bg-cream text-left text-xs uppercase tracking-wide text-ink/55">
          <tr>
            <th className="px-4 py-3">Email</th>
            <th className="px-4 py-3">Role</th>
            <th className="px-4 py-3">Account created</th>
            <th className="px-4 py-3">Last login</th>
          </tr>
        </thead>
        <tbody>
          {isLoading ? (
            <tr><td colSpan={4} className="px-4 py-6 text-ink/55">Loading…</td></tr>
          ) : data?.members.length ? data.members.map((m) => (
            <tr key={m.user_id} className="border-t border-ink/8">
              <td className="px-4 py-3">{m.email}</td>
              <td className="px-4 py-3"><span className="pill">{m.role}</span></td>
              <td className="px-4 py-3">{new Date(m.user_created_at).toLocaleDateString()}</td>
              <td className="px-4 py-3">{m.last_login_at ? new Date(m.last_login_at).toLocaleString() : "—"}</td>
            </tr>
          )) : <tr><td colSpan={4} className="px-4 py-6 text-ink/55">No members.</td></tr>}
        </tbody>
      </table>
    </div>
  );
}

// ── Usage ─────────────────────────────────────────────────────────────────
type WindowKey = "24h" | "7d" | "30d" | "90d";
const WINDOWS: Array<{ key: WindowKey; label: string }> = [
  { key: "24h", label: "Last 24 hours" },
  { key: "7d", label: "Last 7 days" },
  { key: "30d", label: "Last 30 days" },
  { key: "90d", label: "Last 90 days" },
];

function computeRange(key: WindowKey): { from: string; to: string } {
  const to = new Date();
  const from = new Date(to);
  if (key === "24h") {
    from.setTime(to.getTime() - 24 * 60 * 60 * 1000);
  } else {
    const days = key === "7d" ? 7 : key === "30d" ? 30 : 90;
    from.setDate(to.getDate() - days);
    from.setHours(0, 0, 0, 0);
  }
  return { from: from.toISOString(), to: to.toISOString() };
}

function fmtBucket(iso: string, granularity: "day" | "hour"): string {
  const d = new Date(iso);
  return granularity === "hour"
    ? d.toLocaleTimeString([], { hour: "2-digit" })
    : d.toLocaleDateString([], { month: "short", day: "numeric" });
}

// Palette for the per-endpoint stacked bars + legend. Leads with ink + peach
// (brand), then muted complementary tones; cycles if there are more than 8.
const ENDPOINT_COLORS = ["rgb(var(--ink))", "#f59264", "#7a9e7e", "#c9a14a", "#6b8fb5", "#b5746a", "#9a7fb0", "#86a3a8"];
function endpointColor(ep: string, idx: number): string {
  if (ep === "Other") return "#bdb4a6";
  return ENDPOINT_COLORS[idx % ENDPOINT_COLORS.length];
}

interface WindowOption {
  key: string;
  label: string;
  granularity: "day" | "hour";
  range: { from: string; to: string };
}

// Rolling windows + (when the project has a billing cycle) four
// cycle-anchored windows back from paid_until — mirrors the user usage page
// so admin counts line up with what the customer sees.
function buildWindowOptions(paidUntil: string | null, cycleDays: number | null): WindowOption[] {
  const rolling: WindowOption[] = WINDOWS.map((w) => ({
    key: w.key,
    label: w.label,
    granularity: w.key === "24h" ? "hour" : "day",
    range: computeRange(w.key),
  }));
  if (!paidUntil || !cycleDays) return rolling;
  const cycleMs = cycleDays * 24 * 60 * 60 * 1000;
  const resetsAt = new Date(paidUntil);
  const fmt = (d: Date) => d.toLocaleDateString(undefined, { month: "short", day: "numeric" });
  const labels: Record<number, string> = { 0: "Current cycle", 1: "Previous cycle", 2: "2 cycles ago", 3: "3 cycles ago" };
  const cycles: WindowOption[] = [0, 1, 2, 3].map((n) => {
    const to = new Date(resetsAt.getTime() - n * cycleMs);
    const from = new Date(to.getTime() - cycleMs);
    return {
      key: `cycle-${n}`,
      label: `${labels[n]} (${fmt(from)} – ${fmt(to)})`,
      granularity: "day",
      range: { from: from.toISOString(), to: to.toISOString() },
    };
  });
  return [...cycles, ...rolling];
}

function UsageTab({ pid }: { pid: string }) {
  const [windowKey, setWindowKey] = useState<string>("cycle-0");
  const [apiKeyId, setApiKeyId] = useState<string>("");
  const [hoverIdx, setHoverIdx] = useState<number | null>(null);

  const { data: project } = useQuery({ queryKey: ["admin-project", pid], queryFn: () => adminApi.getProject(pid) });
  const { data: keys } = useQuery({ queryKey: ["admin-project-keys", pid], queryFn: () => adminApi.projectKeys(pid) });

  const windowOptions = useMemo(
    () => buildWindowOptions(project?.paid_until ?? null, project?.cycle_days ?? null),
    [project?.paid_until, project?.cycle_days],
  );
  // Falls back to "7d" until the project (and thus cycle windows) loads, or
  // if the project has no cycle.
  const current = windowOptions.find((w) => w.key === windowKey) ?? windowOptions.find((w) => w.key === "7d")!;
  const granularity = current.granularity;
  const range = current.range;

  const { data, isLoading } = useQuery({
    queryKey: ["admin-project-usage", pid, current.key, apiKeyId, range.from, range.to],
    queryFn: () =>
      adminApi.projectUsage(pid, {
        from: range.from,
        to: range.to,
        granularity,
        api_key_id: apiKeyId || undefined,
      }),
  });

  // Top-8 endpoints by total calls (+ "Other") so the stack + legend stay
  // readable; the per-endpoint table below still lists every endpoint.
  const endpoints = useMemo(() => {
    const totals = new Map<string, number>();
    for (const r of data?.rows ?? []) totals.set(r.endpoint, (totals.get(r.endpoint) ?? 0) + r.calls);
    const sorted = [...totals.entries()].sort((a, b) => b[1] - a[1]).map(([e]) => e);
    const TOP = 8;
    return sorted.length > TOP ? [...sorted.slice(0, TOP), "Other"] : sorted;
  }, [data]);

  const perBucket = useMemo(() => {
    const topSet = new Set(endpoints.filter((e) => e !== "Other"));
    const hasOther = endpoints.includes("Other");
    const m = new Map<string, { calls: number; credits: number; byEndpoint: Record<string, number> }>();
    for (const r of data?.rows ?? []) {
      const e = m.get(r.bucket) ?? { calls: 0, credits: 0, byEndpoint: {} };
      e.calls += r.calls;
      e.credits += r.credits;
      const key = topSet.has(r.endpoint) || !hasOther ? r.endpoint : "Other";
      e.byEndpoint[key] = (e.byEndpoint[key] ?? 0) + r.calls;
      m.set(r.bucket, e);
    }
    return [...m.entries()]
      .sort((a, b) => a[0].localeCompare(b[0]))
      .map(([bucket, v]) => ({ bucket, label: fmtBucket(bucket, granularity), ...v }));
  }, [data, granularity, endpoints]);

  const perEndpoint = useMemo(() => {
    const m = new Map<string, { calls: number; credits: number }>();
    for (const r of data?.rows ?? []) {
      const e = m.get(r.endpoint) ?? { calls: 0, credits: 0 };
      e.calls += r.calls;
      e.credits += r.credits;
      m.set(r.endpoint, e);
    }
    return [...m.entries()].map(([endpoint, v]) => ({ endpoint, ...v })).sort((a, b) => b.calls - a.calls);
  }, [data]);

  const maxCalls = Math.max(1, ...perBucket.map((b) => b.calls));
  const totalCalls = perBucket.reduce((s, b) => s + b.calls, 0);

  return (
    <div className="space-y-6">
      <div className="flex flex-wrap items-end gap-3">
        <div>
          <label className="label">Window</label>
          <select className="input mt-1" value={current.key} onChange={(e) => setWindowKey(e.target.value)}>
            {windowOptions.some((w) => w.key.startsWith("cycle")) && (
              <optgroup label="Billing cycle">
                {windowOptions.filter((w) => w.key.startsWith("cycle")).map((w) => (
                  <option key={w.key} value={w.key}>{w.label}</option>
                ))}
              </optgroup>
            )}
            <optgroup label="Rolling">
              {windowOptions.filter((w) => !w.key.startsWith("cycle")).map((w) => (
                <option key={w.key} value={w.key}>{w.label}</option>
              ))}
            </optgroup>
          </select>
        </div>
        <div>
          <label className="label">API key</label>
          <select className="input mt-1" value={apiKeyId} onChange={(e) => setApiKeyId(e.target.value)}>
            <option value="">All keys</option>
            {keys?.keys.map((k) => <option key={k.id} value={k.id}>{k.name}{k.revoked_at ? " (revoked)" : ""}</option>)}
          </select>
        </div>
        <div className="ml-auto flex gap-6 text-sm">
          <Stat label="Requests" value={totalCalls.toLocaleString()} />
          <Stat label="Credits" value={(data?.total_credits ?? 0).toLocaleString()} />
        </div>
      </div>

      <section className="card">
        <div className="flex items-start justify-between gap-4">
          <h2 className="shrink-0 text-sm font-medium">Requests per {granularity === "hour" ? "hour" : "day"}</h2>
          {endpoints.length > 0 && (
            <div className="flex max-w-[65%] flex-wrap justify-end gap-x-3 gap-y-1 text-xs text-ink/55">
              {endpoints.map((ep, i) => (
                <span key={ep} className="flex items-center gap-1.5" title={ep}>
                  <span className="inline-block h-2.5 w-2.5 shrink-0 rounded-sm" style={{ backgroundColor: endpointColor(ep, i) }} />
                  <span className="max-w-[11rem] truncate font-mono text-[0.7rem]">{ep}</span>
                </span>
              ))}
            </div>
          )}
        </div>
        {isLoading ? (
          <p className="mt-4 text-sm text-ink/55">Loading…</p>
        ) : perBucket.length === 0 ? (
          <p className="mt-4 text-sm text-ink/55">No usage in this window.</p>
        ) : (
          <div className="relative mt-4">
            <div className="flex h-48 items-end gap-px" onMouseLeave={() => setHoverIdx(null)}>
              {perBucket.map((b, i) => (
                <div
                  key={b.bucket}
                  className="flex h-full flex-1 cursor-default flex-col justify-end"
                  onMouseEnter={() => setHoverIdx(i)}
                >
                  <div
                    className={"flex flex-col-reverse overflow-hidden rounded-t transition " + (hoverIdx === i ? "opacity-100" : "opacity-90")}
                    style={{ height: `${Math.max(2, Math.round((b.calls / maxCalls) * 100))}%` }}
                  >
                    {endpoints.map((ep, ei) => {
                      const c = b.byEndpoint[ep] ?? 0;
                      if (c === 0) return null;
                      return <div key={ep} style={{ height: `${(c / b.calls) * 100}%`, backgroundColor: endpointColor(ep, ei) }} />;
                    })}
                  </div>
                </div>
              ))}
            </div>
            <div className="mt-2 flex justify-between text-xs text-ink/45">
              <span>{perBucket[0]?.label}</span>
              <span>{perBucket[perBucket.length - 1]?.label}</span>
            </div>

            {hoverIdx != null && perBucket[hoverIdx] && (
              <div
                className="pointer-events-none absolute top-0 z-10 w-60 -translate-x-1/2 -translate-y-full rounded-md bg-ink px-3 py-2 text-xs text-cream shadow-lg"
                style={{ left: `${Math.min(85, Math.max(15, ((hoverIdx + 0.5) / perBucket.length) * 100))}%` }}
              >
                <div className="mb-1 font-medium">{perBucket[hoverIdx].label}</div>
                {endpoints.map((ep, i) => {
                  const c = perBucket[hoverIdx].byEndpoint[ep] ?? 0;
                  if (c === 0) return null;
                  return (
                    <div key={ep} className="flex items-center justify-between gap-3">
                      <span className="flex min-w-0 items-center gap-1.5">
                        <span className="inline-block h-2 w-2 shrink-0 rounded-sm" style={{ backgroundColor: endpointColor(ep, i) }} />
                        <span className="truncate font-mono text-[0.7rem] opacity-80">{ep}</span>
                      </span>
                      <span className="shrink-0">{c.toLocaleString()}</span>
                    </div>
                  );
                })}
                <div className="mt-1 flex justify-between gap-3 border-t border-cream/20 pt-1">
                  <span className="opacity-80">Total</span>
                  <span>{perBucket[hoverIdx].calls.toLocaleString()}</span>
                </div>
                <div className="flex justify-between gap-3">
                  <span className="opacity-80">Credits</span>
                  <span>{perBucket[hoverIdx].credits.toLocaleString()}</span>
                </div>
              </div>
            )}
          </div>
        )}
      </section>

      <section className="card overflow-x-auto p-0">
        <table className="w-full text-sm">
          <thead className="bg-cream text-left text-xs uppercase tracking-wide text-ink/55">
            <tr>
              <th className="px-4 py-3">Endpoint</th>
              <th className="px-4 py-3 text-right">Requests</th>
              <th className="px-4 py-3 text-right">Credits</th>
            </tr>
          </thead>
          <tbody>
            {perEndpoint.length ? perEndpoint.map((e) => (
              <tr key={e.endpoint} className="border-t border-ink/8">
                <td className="px-4 py-3 font-mono text-xs">{e.endpoint}</td>
                <td className="px-4 py-3 text-right">{e.calls.toLocaleString()}</td>
                <td className="px-4 py-3 text-right">{e.credits.toLocaleString()}</td>
              </tr>
            )) : <tr><td colSpan={3} className="px-4 py-6 text-ink/55">No usage.</td></tr>}
          </tbody>
        </table>
      </section>
    </div>
  );
}

// ── Payments ─────────────────────────────────────────────────────────────────
function PaymentsTab({ pid, onChanged }: { pid: string; onChanged: () => void }) {
  const qc = useQueryClient();
  const { data, isLoading } = useQuery({
    queryKey: ["admin-project-payments", pid],
    queryFn: () => adminApi.projectPayments(pid, { limit: 100 }),
  });
  const invalidate = () => {
    onChanged();
    return qc.invalidateQueries({ queryKey: ["admin-project-payments", pid] });
  };
  return (
    <div className="card overflow-x-auto p-0">
      <table className="w-full text-sm">
        <thead className="bg-cream text-left text-xs uppercase tracking-wide text-ink/55">
          <tr>
            <th className="px-4 py-3">Received</th>
            <th className="px-4 py-3">Amount</th>
            <th className="px-4 py-3">Status</th>
            <th className="px-4 py-3">Plan</th>
            <th className="px-4 py-3">Tx</th>
            <th className="px-4 py-3"></th>
          </tr>
        </thead>
        <tbody>
          {isLoading ? (
            <tr><td colSpan={6} className="px-4 py-6 text-ink/55">Loading…</td></tr>
          ) : data?.payments.length ? data.payments.map((p) => (
            <tr key={p.id} className="border-t border-ink/8">
              <td className="px-4 py-3">{new Date(p.received_at).toLocaleString()}</td>
              <td className="px-4 py-3 font-medium">${p.amount_usdc}</td>
              <td className="px-4 py-3">{p.status}</td>
              <td className="px-4 py-3">{p.plan_id ?? "—"}</td>
              <td className="px-4 py-3 font-mono text-xs">{p.tx_signature ? `${p.tx_signature.slice(0, 8)}…` : "—"}</td>
              <td className="px-4 py-3 text-right">
                {p.status === "confirmed" && (
                  <button
                    onClick={async () => {
                      const reason = prompt("Refund reason:");
                      if (reason !== null) {
                        const res = await adminApi.refundPayment(p.id, reason || undefined);
                        await invalidate();
                        alert(
                          res.paid_until_reverted
                            ? `Refunded. paid_until rolled back to ${new Date(res.paid_until!).toLocaleString()}.`
                            : "Refunded. paid_until left unchanged — this payment isn't the active cycle tail (newer payments extended it, or it never advanced the cycle). Adjust paid_until manually if needed.",
                        );
                      }
                    }}
                    className="text-sm text-red-600 hover:text-red-800"
                  >Refund</button>
                )}
                {p.status === "pending" && (
                  <span className="flex justify-end gap-3">
                    <button
                      onClick={async () => {
                        if (confirm("Mark this pending payment as confirmed? This advances paid_until for due renewals.")) {
                          await adminApi.confirmPayment(p.id);
                          await invalidate();
                        }
                      }}
                      className="text-sm text-green-600 hover:text-green-800"
                    >Confirm</button>
                    <button
                      onClick={async () => {
                        const reason = prompt("Reject reason (optional):");
                        if (reason !== null) {
                          await adminApi.rejectPayment(p.id, reason || undefined);
                          await invalidate();
                        }
                      }}
                      className="text-sm text-red-600 hover:text-red-800"
                    >Reject</button>
                  </span>
                )}
              </td>
            </tr>
          )) : <tr><td colSpan={6} className="px-4 py-6 text-ink/55">No payments.</td></tr>}
        </tbody>
      </table>
    </div>
  );
}

// ── IPs ─────────────────────────────────────────────────────────────────────
function IpsTab({ pid }: { pid: string }) {
  const [search, setSearch] = useState("");
  const [debounced, setDebounced] = useState("");
  useEffect(() => {
    const t = setTimeout(() => setDebounced(search.trim()), 300);
    return () => clearTimeout(t);
  }, [search]);

  const { data, isLoading } = useQuery({
    queryKey: ["admin-project-ips", pid, debounced],
    queryFn: () => adminApi.projectIps(pid, { search: debounced || undefined, limit: 200 }),
  });

  return (
    <div className="space-y-4">
      <input
        className="input max-w-xs"
        placeholder="Search IP…"
        value={search}
        onChange={(e) => setSearch(e.target.value)}
      />
      <div className="card overflow-x-auto p-0">
        <table className="w-full text-sm">
          <thead className="bg-cream text-left text-xs uppercase tracking-wide text-ink/55">
            <tr>
              <th className="px-4 py-3">IP</th>
              <th className="px-4 py-3">Country</th>
              <th className="px-4 py-3 text-right">Requests</th>
              <th className="px-4 py-3">First seen</th>
              <th className="px-4 py-3">Last seen</th>
            </tr>
          </thead>
          <tbody>
            {isLoading ? (
              <tr><td colSpan={5} className="px-4 py-6 text-ink/55">Loading…</td></tr>
            ) : data?.ips.length ? data.ips.map((r) => (
              <tr key={r.ip} className="border-t border-ink/8">
                <td className="px-4 py-3 font-mono text-xs">{r.ip}</td>
                <td className="px-4 py-3">{r.country || "—"}</td>
                <td className="px-4 py-3 text-right">{r.calls.toLocaleString()}</td>
                <td className="px-4 py-3">{new Date(r.first_seen).toLocaleString()}</td>
                <td className="px-4 py-3">{new Date(r.last_seen).toLocaleString()}</td>
              </tr>
            )) : <tr><td colSpan={5} className="px-4 py-6 text-ink/55">{debounced ? "No matching IPs." : "No IP activity (last 90 days)."}</td></tr>}
          </tbody>
        </table>
      </div>
    </div>
  );
}

// ── shared bits ──────────────────────────────────────────────────────────────
function Row({ label, value }: { label: string; value: React.ReactNode }) {
  return (
    <div className="flex justify-between border-b border-ink/8 pb-2 last:border-b-0">
      <dt className="text-ink/55">{label}</dt>
      <dd className="text-ink">{value}</dd>
    </div>
  );
}

function Stat({ label, value }: { label: string; value: React.ReactNode }) {
  return (
    <div className="text-right">
      <div className="text-xs uppercase tracking-wider text-ink/45">{label}</div>
      <div className="text-lg font-semibold">{value}</div>
    </div>
  );
}

function RecordPaymentDialog({
  pid, open, onOpenChange, onDone, planOptions,
}: {
  pid: string; open: boolean; onOpenChange: (b: boolean) => void;
  onDone: () => Promise<unknown>; planOptions: string[];
}) {
  const [amount, setAmount] = useState("");
  const [tx, setTx] = useState("");
  const [wallet, setWallet] = useState("");
  const [planId, setPlanId] = useState("");
  const [note, setNote] = useState("");

  const m = useMutation({
    mutationFn: () => adminApi.recordPayment(pid, {
      amount_usdc: Number(amount),
      tx_signature: tx || null,
      from_wallet: wallet || null,
      plan_id: planId || null,
      note: note || null,
    }),
    onSuccess: async () => { await onDone(); onOpenChange(false); setAmount(""); setTx(""); setWallet(""); setNote(""); },
  });

  return (
    <Dialog open={open} onOpenChange={onOpenChange} title="Record payment" description="Extends paid_until by 30 days from now (or current paid_until, whichever is later).">
      <form onSubmit={(e) => { e.preventDefault(); if (amount) m.mutate(); }} className="space-y-3">
        <div>
          <label className="label">Amount (USDC)</label>
          <input className="input mt-1" required type="number" step="0.01" value={amount} onChange={(e) => setAmount(e.target.value)} />
        </div>
        <div>
          <label className="label">Tx signature (optional)</label>
          <input className="input mt-1 font-mono text-xs" value={tx} onChange={(e) => setTx(e.target.value)} />
        </div>
        <div>
          <label className="label">From wallet (optional)</label>
          <input className="input mt-1 font-mono text-xs" value={wallet} onChange={(e) => setWallet(e.target.value)} />
        </div>
        <div>
          <label className="label">Plan (optional)</label>
          <select className="input mt-1" value={planId} onChange={(e) => setPlanId(e.target.value)}>
            <option value="">— none —</option>
            {planOptions.map((p) => <option key={p} value={p}>{p}</option>)}
          </select>
        </div>
        <div>
          <label className="label">Note (optional)</label>
          <input className="input mt-1" value={note} onChange={(e) => setNote(e.target.value)} />
        </div>
        <div className="flex justify-end gap-2 pt-2">
          <button type="button" className="btn-secondary" onClick={() => onOpenChange(false)}>Cancel</button>
          <button type="submit" className="btn-primary" disabled={m.isPending}>{m.isPending ? "Recording…" : "Record"}</button>
        </div>
      </form>
    </Dialog>
  );
}

function TransferOwnerDialog({
  pid, open, onOpenChange, onDone,
}: { pid: string; open: boolean; onOpenChange: (b: boolean) => void; onDone: () => Promise<unknown> }) {
  const [email, setEmail] = useState("");
  const m = useMutation({
    mutationFn: () => adminApi.transferOwner(pid, email.trim().toLowerCase()),
    onSuccess: async () => { await onDone(); onOpenChange(false); setEmail(""); },
  });
  return (
    <Dialog open={open} onOpenChange={onOpenChange} title="Transfer ownership" description="Existing owners are demoted to developer; the target user becomes the sole owner.">
      <form onSubmit={(e) => { e.preventDefault(); if (email.trim()) m.mutate(); }} className="space-y-3">
        <div>
          <label className="label">New owner email</label>
          <input type="email" required className="input mt-1" value={email} onChange={(e) => setEmail(e.target.value)} />
        </div>
        <div className="flex justify-end gap-2 pt-2">
          <button type="button" className="btn-secondary" onClick={() => onOpenChange(false)}>Cancel</button>
          <button type="submit" className="btn-primary" disabled={m.isPending}>{m.isPending ? "Transferring…" : "Transfer"}</button>
        </div>
      </form>
    </Dialog>
  );
}
